import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';

import { DiceRoll, LootEntry, LootItemGroup } from "@dndModels";
import { makeItemMatcher, parseLootRows } from "@dndUtilities/loot_import";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CheckBox from "@components/input/CheckBox";
import LoadingBox from "@components/input/LoadingBox";
import StringBox from "@components/input/strings/StringBox";
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

interface Props {
  item_options: { id: string, name: string }[] | null; // null while the items are loading
  disabled: boolean;
  onImport: (entries: LootEntry[]) => void;
}

// Turns a pasted d100-style table into entries, one per row, matching each row's name to an item.
export default function LootTableImport({
  item_options,
  disabled,
  onImport,
}: Props) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [include_unmatched, setIncludeUnmatched] = useState(true);

  const parsed = parseLootRows(text);
  const match = makeItemMatcher(item_options ?? []);
  const rows = parsed.rows.map(row => ({ row, matches: match(row.name) }));
  const unmatched = rows.filter(o => o.matches.length === 0);
  const importing = include_unmatched ? rows : rows.filter(o => o.matches.length > 0);

  const doImport = () => {
    onImport(importing.map(({ row, matches }) => {
      const entry = new LootEntry();
      entry.weight = row.weight;
      if (matches.length > 0) {
        entry.name = matches[0].name;
        const group = new LootItemGroup();
        group.source = "Specific Item";
        group.item_id = matches[0].id;
        group.count = new DiceRoll({ size: 1, count: row.count });
        entry.item_groups = [group];
      } else {
        entry.name = row.name;
      }
      return entry;
    }));
    setText("");
    setOpen(false);
  };

  const renderContent = () => {
    if (!item_options) {
      return <LoadingBox message="Loading Items" />;
    }
    return (
      <Grid container spacing={1} direction="row">
        <Grid size={12} className="header centered">
          Paste Import
        </Grid>
        <Grid size={12} className="small_label gray">
          Paste one row per line, starting with the roll range, like &quot;01-15 | Potion of Healing&quot;.
          Ranges can use - or –, rows can be split with |, a tab, a colon, or spaces, 00 counts as 100,
          and &quot;2 x Name&quot; gives 2 of it. Each row becomes an entry weighted by how many numbers its range covers.
          Lines that don&apos;t start with a number, like headers, are skipped.
        </Grid>
        <Grid size={12}>
          <StringBox
            name="Rows"
            value={text}
            multiline
            onChange={(changed: string) => setText(changed)}
          />
        </Grid>
        { rows.length > 0 &&
          <>
            <Grid size={12} className="header">
              { rows.length } row(s), { rows.length - unmatched.length } matched to items
            </Grid>
            { rows.map(({ row, matches }) => (
              <Grid key={row.line} size={12}>
                <span style={{ display: "inline-block", width: 70 }}>{ row.lo === row.hi ? row.lo : `${row.lo}-${row.hi}` }</span>
                { row.count > 1 ? `${row.count} x ` : "" }{ row.name }
                { matches.length === 0 && <b style={{ color: "orange" }}> (no item found)</b> }
                { matches.length > 1 && <span className="small_label gray"> ({ matches.length } items have this name, using the first)</span> }
              </Grid>
            ))}
          </>
        }
        { parsed.skipped.length > 0 &&
          <Grid size={12} className="small_label gray">
            Skipped line(s): { parsed.skipped.map(o => o.line).join(", ") }
          </Grid>
        }
        { unmatched.length > 0 &&
          <Grid size={12}>
            <CheckBox
              name={`Also add the ${unmatched.length} unmatched row(s) as entries without items, to fix by hand`}
              value={include_unmatched}
              onChange={setIncludeUnmatched}
            />
          </Grid>
        }
        <Grid size={12}>
          <DandDButtonBox
            name={`Add ${importing.length} Entries`}
            disabled={disabled || importing.length === 0}
            onClick={doImport}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <DandDButtonBox
        name="Paste Import"
        disabled={disabled}
        onClick={() => setOpen(true)}
      />
      { open &&
        <ModalOrDrawer
          open={open}
          width={800}
          onClose={() => { setOpen(false); }}>
          { renderContent() }
        </ModalOrDrawer>
      }
    </>
  );
}
